import { gql, useLazyQuery } from '@provision/graphql-client';
import { useCallback } from 'react';
import { isNullish } from 'remeda';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';

const GET_STRIPE_ACCOUNT_DASHBOARD_LOGIN_URL = gql`
  query GetStripeAccountDashboardLoginUrl($params: GetStripeAccountDashboardLoginUrlInput!) {
    getStripeAccountDashboardLoginUrl(params: $params) {
      url
    }
  }
`;

interface GetStripeAccountDashboardLoginUrlResponse {
  getStripeAccountDashboardLoginUrl: {
    url: string;
  };
}

interface GetStripeAccountDashboardLoginUrlVariables {
  params: {
    partnerId: number;
  };
}

export const useStripeAccountDashboardLoginUrl = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showErrorAlert } = useNotifications();

  const [fetchDashboardLoginUrl, { loading, error }] = useLazyQuery<
    GetStripeAccountDashboardLoginUrlResponse,
    GetStripeAccountDashboardLoginUrlVariables
  >(GET_STRIPE_ACCOUNT_DASHBOARD_LOGIN_URL, {
    fetchPolicy: 'network-only',
    onError: (err) => {
      showErrorAlert(err.message || 'Failed to open Stripe dashboard');
    },
  });

  const getDashboardLoginUrl = useCallback(async () => {
    if (isNullish(selectedPartnerId)) {
      showErrorAlert('Partner not selected');
      return null;
    }

    const result = await fetchDashboardLoginUrl({
      variables: {
        params: {
          partnerId: selectedPartnerId,
        },
      },
    });

    const url = result.data?.getStripeAccountDashboardLoginUrl?.url ?? null;

    if (!url && !result.error) {
      showErrorAlert('Unable to open Stripe dashboard. Please try again.');
    }

    return url;
  }, [fetchDashboardLoginUrl, selectedPartnerId, showErrorAlert]);

  const openStripeDashboard = useCallback(async () => {
    if (isNullish(selectedPartnerId)) {
      showErrorAlert('Partner not selected');
      return;
    }

    // Open synchronously while the click gesture is active so popup blockers allow it.
    // Do not pass noopener here: with noopener, window.open always returns null even on success.
    const newTab = window.open('about:blank', '_blank');

    if (!newTab) {
      showErrorAlert('Unable to open Stripe dashboard in a new tab.');
      return;
    }

    newTab.opener = null;

    const url = await getDashboardLoginUrl();

    if (!url) {
      newTab.close();
      return;
    }

    newTab.location.href = url;
  }, [getDashboardLoginUrl, selectedPartnerId, showErrorAlert]);

  return {
    getDashboardLoginUrl,
    openStripeDashboard,
    loading,
    error,
  };
};
